/**
 * Adapter for WC's shipped storefront widgets. WC remains the only wishlist
 * persistence/subscription implementation. No private API or second local store.
 * See docs/integrations/wc-wishlist.md for the verified vendor contract.
 */
import { StandardEvents } from '@shopify/events';

let controlId = 0;
// How long a variant switch waits for WC to render before giving up. WC mounts
// in ~0.3–1.1 s: its first refresh, or the forced pass after its 1 s throttle.
const actionsReadyTimeoutMs = 3500;
// How long a page load waits for WC's script and first render on the PDP
// (TcsProductActions' unavailable note). Cards read their page-load wait from
// --tcs-restock-load-timeout (tcs-wishlist.css, which also drives their no-JS
// swap) and use this constant only if that property cannot be read. So the 10 s
// is declared twice, here and in tcs-wishlist.css, and the two must stay equal.
// The property is scoped to the card's .tcs-restock; the PDP keeps this constant
// so its behaviour is untouched.
const actionsLoadTimeoutMs = 10000;

// A live region only announces reliably when it exists before its content
// does, and the same message must be cleared first to be spoken again.
function announce(region, text) {
  if (!region || !text) return;
  region.textContent = '';
  setTimeout(() => {
    region.textContent = text;
  }, 50);
}

function latestAlert(root) {
  const alerts = root?.querySelectorAll?.('.wcBisAlert');
  return alerts?.[alerts.length - 1] ?? null;
}

function alertText(alert) {
  return alert?.textContent.trim() ?? '';
}

// WC owns the wishlist-page remove button and its network handler. Keep that
// control intact, but expose the leading action word from its accessible name
// so the icon-only action is visually discoverable. The marker makes this
// idempotent across WC's React re-renders.
function labelWishlistRemoveButtons(root = document) {
  for (const button of root.querySelectorAll?.('.wishlistClubPage .wcProductTrigger') ?? []) {
    if (button.hasAttribute('data-tcs-remove-labeled')) continue;
    const accessibleName = button.getAttribute('aria-label')?.trim();
    const visibleLabel = accessibleName?.split(/\s+/)[0];
    if (!visibleLabel) continue;
    const label = document.createElement('span');
    label.className = 'tcs-wishlist-remove-label';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = visibleLabel;
    button.append(label);
    button.setAttribute('data-tcs-remove-labeled', '');
  }
}

// WC injects an icon link to its app-proxy page beside the header cart link.
// Its only text is an item count, when WC has filled one in. Reuse the
// server-rendered TCS wishlist-link name, keep a visible count in the name
// (WCAG 2.5.3), and leave WC's href, classes, icon and click behavior
// untouched. A name WC supplies itself is never replaced. The adapter runs
// again after vendor renders, so the count in the name follows WC's.
function labelWishlistPageLinks(root = document) {
  const label = document
    .querySelector?.('.tcs-wishlist-link[aria-label]')
    ?.getAttribute('aria-label')
    ?.trim();
  if (!label) return;

  for (const link of root.querySelectorAll?.('a.wc_wishlistIcon[href*="/apps/wishlist"]') ?? []) {
    const current = link.getAttribute('aria-label')?.trim();
    if (current && current !== link.dataset?.tcsWishlistName) continue;
    const count = link.textContent?.trim();
    const name = count ? `${label} (${count})` : label;
    if (current === name) continue;
    link.setAttribute('aria-label', name);
    if (link.dataset) link.dataset.tcsWishlistName = name;
  }
}

function allowRestockSelection() {
  const main = document.querySelector('#MainContent[data-template^="product"]');
  if (!main?.querySelector('tcs-product-actions[data-restock-enabled]')) return;
  for (const input of main.querySelectorAll('variant-picker input[aria-disabled="true"]')) {
    if (!input.closest('product-card, quick-add-dialog')) input.removeAttribute('aria-disabled');
  }
}

class TcsWishlist extends HTMLElement {
  static observedAttributes = ['data-variant-id'];
  #observer;

  connectedCallback() {
    this.#observer = new MutationObserver(() => this.#nameButton());
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    this.#nameButton();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  attributeChangedCallback(name, oldValue, value) {
    if (!oldValue || oldValue === value) return;
    const mount = this.querySelector('[data-variant_id]');
    if (!mount) return;
    mount.dataset.variant_id = value;
    // Card shade changes don't replace the mount, so refresh its vendor root.
    if (typeof window.wcPacificWishlistWidget === 'function') {
      window.wcPacificWishlistWidget(
        this.classList.contains('tcs-wishlist--card') ? '.th_wl_col_btn' : '.th_prd_wl_btn',
        this.dataset.productId,
        this.classList.contains('tcs-wishlist--card') ? 'collection' : 'product',
      );
    }
  }

  #nameButton() {
    const button = this.querySelector('.wc_wishlistBlock');
    if (!button) return;
    // Vendor IDs repeat when a product appears in several rails.
    if (!button.id.startsWith('tcs-wishlist-')) button.id = `tcs-wishlist-${++controlId}`;
    // WC changes its visible product label when saved. Keep those exact words
    // in the accessible name for speech input; card hearts are icon-only.
    const visibleLabel = this.classList.contains('tcs-wishlist--card')
      ? ''
      : button.querySelector('.wc_wishlistText')?.textContent.trim();
    button.setAttribute(
      'aria-label',
      visibleLabel ? `${visibleLabel} — ${this.dataset.productTitle}` : this.dataset.label,
    );
    button.setAttribute('aria-pressed', String(button.classList.contains('isActive')));
  }

  get ready() {
    return !!this.querySelector('.wc_wishlistBlock');
  }

  get control() {
    return this.querySelector('.wc_wishlistBlock');
  }
}

// WC back-in-stock mount. On the PDP, WC's own button is the control. On cards the
// TCS Notify me button (the proxy) is the control: server-rendered, so it shows in
// the same frame as the page or the swatch press, while WC's button renders out of
// view and the proxy activates it. Evidence (wishlist-club-261 bundle, sha256
// 02b395b2…a4486472, read 2026-09-29): WC fetches back-in-stock data per PRODUCT
// (`getBisProducts({product_ids})`, every variant, cached in `wcBisProductData`),
// then `wcPacificBisWidget(productId)` renders each mount for the `data-variant_id`
// it carries AT RENDER TIME; the dialog's variant is fixed from that render
// (`useState(selectedData.variant_id)`). So a shade change must re-render (the
// same `data-variant_id` write + `wcPacificBisWidget` call WC's own product page
// makes), and a stale button for the previous shade must never be pressed.
class TcsRestock extends HTMLElement {
  #observer;
  #timer;
  #deadline = 0;
  #forced;
  #onLoad;
  #busy = false;
  #lastFocus = null;
  #stranded = 0;
  #mirrored = '';
  #mirroredAlert;

  connectedCallback() {
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.#proxy()?.addEventListener('click', this.#onProxyClick);
    this.#nameProxy();
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
    // A late upgrade (after the no-JS swap showed the sold-out link) never leaves
    // the slot empty: the proxy returns in the same task, and #wait falls back at
    // once when WC is absent after load.
    this.#sync();
    if (!this.#vendorButton()) this.#wait();
  }

  disconnectedCallback() {
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.#proxy()?.removeEventListener('click', this.#onProxyClick);
    this.#observer?.disconnect();
    // A press cannot outlive the card: a slider or rail that detaches and
    // re-attaches it must not open a dialog nobody is waiting for.
    this.cancel();
    clearTimeout(this.#timer);
    clearTimeout(this.#forced);
    this.#timer = undefined;
    this.#forced = undefined;
    if (this.#onLoad) window.removeEventListener('load', this.#onLoad);
    this.#onLoad = undefined;
  }

  #onFocusIn = (event) => {
    this.#lastFocus = event.target;
    this.#stranded = 0;
  };

  // Focus that moves to another element is the shopper's choice. Focus that
  // simply vanishes (relatedTarget null) is what a vendor re-render causes.
  #onFocusOut = (event) => {
    if (!event.relatedTarget) {
      this.#stranded = Date.now();
      return;
    }
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.#lastFocus = null;
      this.#stranded = 0;
    }
  };

  // Focus was inside this mount but its element was removed or hidden.
  #focusStranded() {
    const last = this.#lastFocus;
    if (!last) return false;
    const active = document.activeElement;
    const adrift = !active || active === document.body || active === last;
    const gone =
      !last.isConnected ||
      !!last.closest('[hidden]') ||
      window.getComputedStyle(last).display === 'none';
    return adrift && gone;
  }

  // WC's button for THIS variant. WC renders `data-variant_id` on it; after a card
  // changes shade, the previous shade's button stays until WC re-renders.
  #vendorButton() {
    const button = this.querySelector('.wc_wl_bis_btn button');
    if (!button) return null;
    const variant = button.getAttribute('data-variant_id');
    return variant == null || variant === this.dataset.variantId ? button : null;
  }

  #proxy() {
    return this.querySelector('[data-tcs-restock-proxy]');
  }

  #fallback() {
    return this.querySelector('[data-tcs-restock-fallback]');
  }

  get #fallen() {
    return this.getAttribute('data-tcs-restock-state') === 'fallback';
  }

  #control() {
    if (!this.#proxy()) return this.#vendorButton();
    return this.#fallen ? this.#fallback() : this.#proxy();
  }

  get ready() {
    return !!this.#control();
  }

  get control() {
    return this.#control();
  }

  // "Notify me — Product — Shade", from the host's locale label.
  #nameProxy() {
    const proxy = this.#proxy();
    if (!proxy) return;
    const title = this.dataset.variantTitle;
    const variant = title && title !== 'Default Title' ? ` — ${title}` : '';
    proxy.setAttribute(
      'aria-label',
      `${this.dataset.label} — ${this.dataset.productTitle}${variant}`,
    );
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('data-tcs-restock-busy', busy);
  }

  // Pressing the proxy presses WC's button, so WC opens its own dialog for the
  // selected variant. If WC has not rendered it yet, wait silently (the dialog
  // opening is the feedback) and press it when it arrives; if it never does, the
  // shopper asked to act, so go to the product page (#fallBack).
  #onProxyClick = (event) => {
    event.preventDefault();
    if (this.#fallen) return;
    const button = this.#vendorButton();
    if (button) {
      button.click();
      return;
    }
    this.#setBusy(true);
    // The shopper is waiting: never longer than the variant-switch timeout
    // before the product page, even while WC's script is still loading.
    if (!this.#timer || this.#deadline - Date.now() > actionsReadyTimeoutMs)
      this.#wait(actionsReadyTimeoutMs);
    window.$th_refreshWishlistItems?.();
  };

  // Drop a press still waiting for WC: the card chose an in-stock shade (or left
  // the page), so opening the dialog, or the product page, would answer a
  // question the shopper no longer asks.
  cancel() {
    if (!this.#busy) return;
    this.#setBusy(false);
    releaseModalTrigger(this);
  }

  // Product-card swatches keep one WC mount and retarget it to the exact
  // sold-out variant. The proxy shows at once; WC re-renders from its cached
  // product data (the card's mount was rendered at page load), and the
  // throttled refresh plus a forced pass cover a product WC has not fetched yet.
  selectVariant(variantId, variantTitle = '') {
    if (!variantId || this.dataset.variantId === String(variantId)) return;
    const mount = this.querySelector('.wc_wl_bis_btn');
    if (!mount) return;

    this.dataset.variantId = String(variantId);
    this.dataset.variantTitle = variantTitle;
    mount.dataset.variant_id = String(variantId);
    this.#nameProxy();
    // A press still waiting was for the previous shade.
    this.#setBusy(false);
    this.#showProxy();
    if (!this.#vendorButton()) this.#wait();

    if (typeof window.wcPacificBisWidget === 'function')
      window.wcPacificBisWidget(this.dataset.productId);
    window.$th_refreshWishlistItems?.();
    clearTimeout(this.#forced);
    this.#forced = setTimeout(() => {
      this.#forced = undefined;
      window.$th_refreshWishlistItems?.(true);
    }, 1100);
  }

  // Called when WC's dialog closed and the control that opened it is gone.
  restoreFocus() {
    const control = this.#control();
    if (control) {
      control.focus();
      return;
    }
    // Nothing focusable survived: park focus on the host itself.
    this.setAttribute('tabindex', '-1');
    this.addEventListener('blur', () => this.removeAttribute('tabindex'), { once: true });
    this.focus();
  }

  // Wait for WC's button for the selected variant: actionsReadyTimeoutMs (3.5 s,
  // as the PDP variant switch) with WC's script present — it renders within
  // ~1.1 s, or ~2 s when it still has to fetch the product; the page-load timeout
  // (#loadTimeout, 10 s) while it is not. WC's app-embed script runs before the
  // window `load` event, so WC still absent at `load` is blocked or switched off:
  // re-checked then, and the sold-out link shows at once.
  #wait(delay) {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    if (!this.#proxy()) return;
    const vendor = typeof window.$th_refreshWishlistItems === 'function';
    if (!vendor && document.readyState === 'complete') {
      this.#fallBack();
      return;
    }
    if (!vendor && !this.#onLoad) {
      this.#onLoad = () => {
        this.#onLoad = undefined;
        if (typeof window.$th_refreshWishlistItems !== 'function' && !this.#vendorButton())
          this.#fallBack();
      };
      window.addEventListener('load', this.#onLoad, { once: true });
    }
    const wait = delay ?? (vendor ? actionsReadyTimeoutMs : this.#loadTimeout());
    this.#deadline = Date.now() + wait;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      if (!this.#vendorButton()) this.#fallBack();
    }, wait);
  }

  // The cards' page-load timeout: the CSS custom property that also drives
  // their no-JS swap (tcs-wishlist.css), read as a CSS time. Falls back to
  // actionsLoadTimeoutMs (the same 10 s, declared for the PDP).
  #loadTimeout() {
    const value = getComputedStyle(this).getPropertyValue('--tcs-restock-load-timeout').trim();
    const match = /^([\d.]+)(m?s)$/.exec(value);
    if (!match) return actionsLoadTimeoutMs;
    return Number(match[1]) * (match[2] === 's' ? 1000 : 1);
  }

  // WC cannot provide a control: the honest sold-out link to the product page
  // takes the button's cell. Focus moves with it only if it was on the button.
  #fallBack() {
    const fallback = this.#fallback();
    if (!fallback) return;
    clearTimeout(this.#timer);
    this.#timer = undefined;
    const busy = this.#busy;
    this.#setBusy(false);
    const hadFocus = document.activeElement === this.#proxy();
    this.setAttribute('data-tcs-restock-state', 'fallback');
    releaseModalTrigger(this);
    if (busy) {
      window.location.assign(fallback.href);
      return;
    }
    if (hadFocus) {
      this.#lastFocus = fallback;
      fallback.focus({ preventScroll: true });
    }
  }

  #showProxy() {
    if (!this.#fallen) return;
    const hadFocus = document.activeElement === this.#fallback();
    this.removeAttribute('data-tcs-restock-state');
    if (hadFocus) this.#proxy()?.focus({ preventScroll: true });
  }

  #sync() {
    this.#mirrorAlert();
    const button = this.#vendorButton();
    if (!button) {
      if (this.#focusStranded()) this.#stranded = Date.now();
      // WC can drop its control while it applies its own eligibility rules; wait
      // again so a card is never left promising a dialog WC will not open.
      if (!this.#timer && !this.#fallen) this.#wait();
      return;
    }
    clearTimeout(this.#timer);
    this.#timer = undefined;
    const visibleLabel = button.textContent.trim() || this.dataset.label;
    const variant =
      this.dataset.variantTitle && this.dataset.variantTitle !== 'Default Title'
        ? ` — ${this.dataset.variantTitle}`
        : '';
    button.setAttribute('aria-label', `${visibleLabel} — ${this.dataset.productTitle}${variant}`);
    if (this.#proxy()) {
      // WC's button is out of view on cards: the proxy keeps focus and name.
      this.#showProxy();
      this.#stranded = 0;
      if (this.#busy) {
        this.#setBusy(false);
        button.click();
      }
      return;
    }
    // Keep the shopper's place when the control under their focus is replaced:
    // WC re-renders its button.
    const wasStranded = this.#stranded && Date.now() - this.#stranded < 3000;
    if ((wasStranded || this.#focusStranded()) && button !== this.#lastFocus) {
      button.focus({ preventScroll: true });
    }
    this.#stranded = 0;
  }

  // WC's own alert is left untouched; its text is mirrored into a region that
  // existed before the text did.
  #mirrorAlert() {
    const alert = latestAlert(this);
    const text = alertText(alert);
    if (alert === this.#mirroredAlert && text === this.#mirrored) return;
    this.#mirroredAlert = alert;
    this.#mirrored = text;
    announce(this.querySelector('.tcs-restock__status'), text);
  }
}

class TcsProductActions extends HTMLElement {
  #observer;
  #timer;
  #section;
  #request = 0;
  #stage;

  connectedCallback() {
    allowRestockSelection();
    this.#section = this.closest('.shopify-section');
    if (this.getAttribute?.('data-tcs-actions-stage') == null)
      this.#section?.addEventListener(StandardEvents.productSelect, this.#selectVariant);
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
    this.#sync();
    if (this.getAttribute?.('data-tcs-actions-stage') == null) {
      this.#timer = setTimeout(() => {
        this.#showUnavailable(!this.#controlsReady);
      }, actionsLoadTimeoutMs);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#selectVariant);
    this.#stage?.remove();
  }

  #selectVariant = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card, quick-add-dialog')
    )
      return;
    const request = ++this.#request;
    const focus = this.#focusTarget();
    this.#stage?.remove();
    this.inert = true;
    this.setAttribute('aria-busy', 'true');
    event.promise
      .then(async ({ detail }) => {
        if (request !== this.#request || !this.isConnected) return;
        const next = detail?.html?.querySelector(
          `tcs-product-actions[data-block-id="${this.dataset.blockId}"]`,
        );
        if (!next) throw new Error('No resolved wishlist variant');
        this.#stageNext(next);
        // WC's documented observer mounts new product and restock placeholders.
        window.$th_refreshWishlistItems?.();
        // WC throttles refresh work for one second. A section replacement can
        // land inside that window, so retry after it closes and force the
        // current product/variant mounts to be reconciled.
        setTimeout(() => window.$th_refreshWishlistItems?.(true), 1100);
        await this.#waitForReady(next);
        if (request !== this.#request || !this.isConnected) {
          next.remove();
          return;
        }
        this.#swapReady(next, focus);
      })
      .catch(() => {
        if (request !== this.#request || !this.isConnected) return;
        this.#failActive();
      });
  };

  get #ready() {
    return this.#controlsReady || this.#statusReady;
  }

  get #controlsReady() {
    const wishlist = this.querySelector('tcs-wishlist');
    const restock = this.querySelector('tcs-restock');
    return !!(wishlist || restock) && (wishlist?.ready ?? true) && (restock?.ready ?? true);
  }

  get #statusReady() {
    const note = this.querySelector('.tcs-product-actions__unavailable');
    return !!note?.textContent.trim() && !note.classList.contains('visually-hidden');
  }

  #stageNext(next) {
    next.setAttribute('data-tcs-actions-stage', '');
    next.setAttribute('aria-hidden', 'true');
    next.inert = true;
    Object.assign(next.style, {
      position: 'absolute',
      inlineSize: '1px',
      blockSize: '1px',
      overflow: 'hidden',
      clipPath: 'inset(50%)',
      whiteSpace: 'nowrap',
    });
    this.after(next);
    this.#stage = next;
  }

  #disableControls() {
    for (const control of this.querySelectorAll('tcs-wishlist, .tcs-restock')) {
      control.inert = true;
    }
  }

  #failActive() {
    this.#stage?.remove();
    this.#stage = undefined;
    this.inert = false;
    this.removeAttribute('aria-busy');
    this.#disableControls();
    this.#showUnavailable(true);
  }

  #waitForReady(next) {
    if (next.#ready) return Promise.resolve();
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      };
      const observer = new MutationObserver(() => {
        if (next.#ready) finish();
      });
      observer.observe(next, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
      const timer = setTimeout(() => {
        next.querySelector('tcs-wishlist')?.remove();
        next.querySelector('.tcs-restock')?.remove();
        next.#showUnavailable(true);
        finish();
      }, actionsReadyTimeoutMs);
    });
  }

  #focusTarget() {
    const active = document.activeElement;
    if (!(active instanceof Element) || !this.contains(active)) return null;
    if (active.closest('tcs-wishlist')) return { type: 'wishlist' };
    if (active.closest('tcs-restock')) return { type: 'restock' };
    return { type: 'host' };
  }

  #restoreFocus(next, focus) {
    if (!focus) return;
    const active = document.activeElement;
    if (
      active instanceof Element &&
      active !== document.body &&
      active !== this &&
      !this.contains(active)
    )
      return;
    const target =
      focus.type === 'wishlist'
        ? next.querySelector('tcs-wishlist')?.control
        : focus.type === 'restock'
          ? next.querySelector('tcs-restock')?.control
          : next;
    if (target) target.focus?.({ preventScroll: true });
  }

  #swapReady(next, focus) {
    next.removeAttribute('data-tcs-actions-stage');
    next.removeAttribute('aria-hidden');
    next.inert = false;
    next.style.position = '';
    next.style.inlineSize = '';
    next.style.blockSize = '';
    next.style.overflow = '';
    next.style.clipPath = '';
    next.style.whiteSpace = '';
    next.removeAttribute('aria-busy');
    this.#restoreFocus(next, focus);
    this.#stage = undefined;
    this.replaceWith(next);
  }

  // The <p> is a live region that renders empty, so text added later is
  // announced. Its text comes from the host so it stays a locale string.
  #showUnavailable(show) {
    const note = this.querySelector('.tcs-product-actions__unavailable');
    if (!note) return;
    note.textContent = show ? (this.dataset.unavailableText ?? '') : '';
    note.classList.toggle('visually-hidden', !show);
  }

  #sync() {
    if (this.#controlsReady) this.#showUnavailable(false);
  }
}

// WC's dialog traps Tab at its first/last control and closes on Escape, but it
// does not return focus (and its re-render can destroy the opener). Restore it
// when the dialog disappears.
let modalTrigger;
let modalHost;
let modal;
let modalStatus;
let modalMirrored = '';
let modalAlert;
let modalId = 0;
let modalBaseEmailDescription = '';

// The host survives a card re-render; the PDP replaces it, so match by identity.
const hostFor = (host, ids) =>
  host?.isConnected
    ? host
    : document.querySelector(
        `tcs-restock[data-product-id="${ids?.product}"][data-variant-id="${ids?.variant}"]`,
      );

let modalIds;
// A press on a card's Notify me records it as the dialog's opener. If that press
// is cancelled or falls back, no WC dialog is coming from it: forget it, so an
// unrelated WC dialog never returns focus to a stale card.
function releaseModalTrigger(host) {
  if (modalHost !== host || modal) return;
  modalTrigger = null;
  modalHost = null;
  modalIds = undefined;
}
document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target.closest?.('tcs-restock button');
    if (!trigger) return;
    modalHost = trigger.closest('tcs-restock');
    // On cards the proxy presses WC's out-of-view button; focus returns to the proxy.
    modalTrigger = modalHost.querySelector('[data-tcs-restock-proxy]') ?? trigger;
    modalIds = { product: modalHost.dataset.productId, variant: modalHost.dataset.variantId };
  },
  true,
);

function nameModal(dialog) {
  if (dialog.hasAttribute('aria-labelledby') || dialog.hasAttribute('aria-label')) return;
  // WC's dialog renders its title in an h3 and the product in an h4.
  const headings = [...dialog.querySelectorAll('h3, h4')].filter((h) => h.textContent.trim());
  if (headings.length) {
    for (const heading of headings) if (!heading.id) heading.id = `tcs-wc-modal-${++modalId}`;
    dialog.setAttribute('aria-labelledby', headings.map((h) => h.id).join(' '));
    return;
  }
  const { label, productTitle, variantTitle } = modalHost?.dataset ?? {};
  const variant = variantTitle && variantTitle !== 'Default Title' ? ` — ${variantTitle}` : '';
  dialog.setAttribute('aria-label', [label, productTitle].filter(Boolean).join(' — ') + variant);
}

function ensureId(element, prefix) {
  if (!element.id) element.id = `${prefix}-${++modalId}`;
  return element.id;
}

function updateEmailDescription(email, { invalid = false } = {}) {
  if (!email) return;
  const ids = new Set(modalBaseEmailDescription.split(/\s+/).filter(Boolean));
  if (invalid && modalStatus) ids.add(ensureId(modalStatus, 'tcs-wc-modal-status'));
  if (ids.size) email.setAttribute('aria-describedby', [...ids].join(' '));
  else email.removeAttribute('aria-describedby');
}

function modalEmail() {
  if (!modal) return null;
  const selectors = [
    'input[name="bis_email"]',
    'input[type="email"]',
    'input[autocomplete="email"]',
    'input[name*="email" i]',
    'input[id*="email" i]',
    'input[placeholder*="email" i]',
  ];
  for (const selector of selectors) {
    const input = modal.querySelector(selector);
    if (input) return input;
  }
  return modal.querySelector('.wcWishlistCreate input, .wcBisContain input');
}

function setupModalEmail(email) {
  if (!email) return;
  email.type = 'email';
  email.required = true;
  email.autocomplete = 'email';
  email.inputMode = 'email';
  if (!email.id) email.id = `tcs-wc-modal-email-${++modalId}`;
  if (!email.hasAttribute('data-tcs-restock-describedby-ready')) {
    email.setAttribute('data-tcs-restock-describedby-ready', 'true');
    modalBaseEmailDescription = email.getAttribute('aria-describedby') ?? '';
    const instruction = [
      ...modal.querySelectorAll('.wcWishlistModal_header p, .wcWishlistModal_body p'),
    ].find((node) => node.textContent.trim());
    if (instruction) {
      modalBaseEmailDescription = [
        modalBaseEmailDescription,
        ensureId(instruction, 'tcs-wc-modal-help'),
      ]
        .filter(Boolean)
        .join(' ');
    }
  }
  updateEmailDescription(email, { invalid: email.getAttribute('aria-invalid') === 'true' });
}

// WC's Notify me variant chooser. Shipped wishlist-club-261 builds it as
// <select aria-label="Select Variant" name="variants" className="wcBisSelect"> inside
// .wcBisInfo, one option per sold-out variant labelled with the Shopify variant title
// (read-only inspection of wishlistclub.min.js, sha256 02b395b2…, 2026-09-29). Either
// the name or the class matches, so a later build that drops one still resolves.
// The dialog holds no other <select>.
function syncModalVariantChoice(dialog) {
  const select = dialog.querySelector('select[name="variants"], select.wcBisSelect');
  if (!select) return;
  const singleDefault =
    select.options.length === 1 && select.options[0].textContent.trim() === 'Default Title';
  select.hidden = singleDefault;
  select.toggleAttribute('data-tcs-single-default', singleDefault);
}

function clearModalEmailInvalid(email) {
  email?.removeAttribute('aria-invalid');
  updateEmailDescription(email);
}

function setModalBusy(busy) {
  if (!modal) return;
  if (busy) modal.setAttribute('aria-busy', 'true');
  else modal.removeAttribute('aria-busy');
  modal.dataset.tcsRestockSubmitting = busy ? 'true' : 'false';
  const submit = modal.querySelector('.wcBisBtn');
  if (submit) {
    if (!busy) submit.disabled = false;
    submit.setAttribute('aria-disabled', String(busy));
  }
}

function completeModalSubmit(text) {
  if (!text) return;
  setModalBusy(false);
}

document.addEventListener(
  'click',
  (event) => {
    const submit = event.target.closest?.('.wcWishlistModal .wcBisBtn');
    if (!submit || !modal?.contains(submit)) return;
    if (modal.dataset.tcsRestockSubmitting === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const email = modalEmail();
    if (email && !email.checkValidity()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      email.setAttribute('aria-invalid', 'true');
      updateEmailDescription(email, { invalid: true });
      modalStatus?.setAttribute('role', 'alert');
      modalStatus?.setAttribute('aria-live', 'assertive');
      announce(modalStatus, email.validationMessage);
      email.focus();
      email.reportValidity?.();
      return;
    }
    modalStatus?.setAttribute('role', 'status');
    modalStatus?.removeAttribute('aria-live');
    clearModalEmailInvalid(email);
    setModalBusy(true);
  },
  true,
);

new MutationObserver(() => {
  allowRestockSelection();
  labelWishlistRemoveButtons();
  labelWishlistPageLinks();
  const next = document.querySelector('.wcWishlistModal[role="dialog"]');
  if (next && modalTrigger) {
    modal = next;
    modal.classList.add('tcs-wc-modal');
    // WC renders the dialog aria-hidden="true", which removes it and its
    // focused controls from the accessibility tree.
    modal.removeAttribute('aria-hidden');
    modal.setAttribute('aria-modal', 'true');
    nameModal(modal);
    syncModalVariantChoice(modal);
    // aria-modal hides everything outside the dialog, so the result is
    // mirrored into a status region created with the dialog, before WC's alert.
    if (!modalStatus?.isConnected) {
      modalStatus = document.createElement('div');
      modalStatus.className = 'tcs-wc-modal__status visually-hidden';
      modalStatus.setAttribute('role', 'status');
      ensureId(modalStatus, 'tcs-wc-modal-status');
      modal.append(modalStatus);
      modalMirrored = '';
      modalAlert = undefined;
    }
    setupModalEmail(modalEmail());
    const submit = modal.querySelector('.wcBisBtn');
    if (submit && modal.dataset.tcsRestockSubmitting !== 'true') submit.disabled = false;
    const alert = latestAlert(modal);
    const text = alertText(alert);
    if (alert !== modalAlert || text !== modalMirrored) {
      modalAlert = alert;
      modalMirrored = text;
      announce(modalStatus, text);
      completeModalSubmit(text);
    }
  } else if (modal && !modal.isConnected) {
    if (modalTrigger?.isConnected) modalTrigger.focus();
    else hostFor(modalHost, modalIds)?.restoreFocus();
    modal = null;
    modalStatus = null;
    modalMirrored = '';
    modalAlert = undefined;
    modalBaseEmailDescription = '';
    modalTrigger = null;
    modalHost = null;
  }
}).observe(document.body, { childList: true, subtree: true });

labelWishlistRemoveButtons();
labelWishlistPageLinks();

// Defined last: defining upgrades elements already in the page synchronously,
// and their callbacks read the module state above (modalHost and friends), which
// would still be in its temporal dead zone if the definitions came first.
if (!customElements.get('tcs-wishlist')) customElements.define('tcs-wishlist', TcsWishlist);
if (!customElements.get('tcs-restock')) customElements.define('tcs-restock', TcsRestock);
if (!customElements.get('tcs-product-actions'))
  customElements.define('tcs-product-actions', TcsProductActions);
